import { apiClient } from "@/api/api-client";
import { getCustomer } from "@/api/customer-api";
import { PaymentDialogValues } from "@/components/orders/PaymentDialog";
import { ApiResponse, PageContent } from "@/types/api-response";
import {
  CancelOrder,
  ChatOrderRequest,
  CommentOrderRequest,
  OrderCreateFormValues,
  OrderCreateRequest,
  OrderHistory,
  OrderItemCreateRequest,
  OrderReceiptResponse,
  OrderReferenceItem,
  OrderResponse,
  OrderSortOption,
  OrderStateHistory,
  OrderStatus,
  OrderSummaryDaily,
  OrderUpdateValues,
  PaymentRecord,
  TOrderWithRelation,
  UpdateOrderItem,
} from "@/types/order-type";
import { User } from "@/types/user";

// Convert form values to API request format
function formatOrderCreateRequest(
  formValues: OrderCreateFormValues,
  customerName: string
): OrderCreateRequest {
  if (!customerName) {
    throw new Error("Customer name is required");
  }

  const request: OrderCreateRequest = {
    customerId: formValues.customerId,
    customerName: customerName,
    status: formValues.status || OrderStatus.DRAFT,
    notes: formValues.notes || undefined,
    fulfillment: {
      method: formValues.fulfillment.method,
      carrier: formValues.fulfillment.carrier,
      price: formValues.fulfillment.price,
    },
    items: (formValues.items || []).map(
      (i): OrderItemCreateRequest => ({
        variantId: i.variantId,
        quantity: i.quantity,
      })
    ),
  };

  return request;
}

// Create a new order
export async function createOrder(
  order: OrderCreateFormValues
): Promise<ApiResponse<OrderResponse>> {


  if (!order.customerId) {
    throw new Error("Customer ID is required");
  }

  // Fetch customer to get customerName
  const customerResponse = await getCustomer(order.customerId);
  if (!customerResponse.success || !customerResponse.data) {
    throw new Error(
      customerResponse.message || "Failed to fetch customer information"
    );
  }

  const customerName = customerResponse.data.name;
  if (!customerName) {
    throw new Error("Customer name is not available");
  }

  const requestData = formatOrderCreateRequest(order, customerName);
  const response = await apiClient.post<OrderResponse, OrderCreateRequest>(
    "/orders",
    requestData
  );
  return response;
}

// Get orders with pagination
export async function getOrders(params?: {
  page?: number;
  size?: number;
  searchTerm?: string;
  sort?: OrderSortOption;
  status?: string;
  customerId?: string;
  orderDate?: string;
}): Promise<ApiResponse<PageContent<OrderResponse>>> {
  const response = await apiClient.get<PageContent<OrderResponse>, object>(
    "/orders",
    params || {}
  );
  return response;
}

// Get a single order by ID
export async function getOrder(
  id: string
): Promise<ApiResponse<OrderResponse>> {
  const response = await apiClient.get<OrderResponse, object>(`/orders/${id}`);
  return response;
}

export async function getOrderWithRelation(
  id: string
): Promise<ApiResponse<TOrderWithRelation>> {
  return await apiClient.get<TOrderWithRelation, object>(
    `/orders/${id}/relation`
  );
}

// Update an existing order
export async function updateOrder(
  id: string,
  order: OrderUpdateValues
): Promise<ApiResponse<OrderResponse>> {
  const response = await apiClient.put<OrderResponse, OrderUpdateValues>(
    `/orders/${id}`,
    order
  );
  return response;
}

// Delete an order (soft delete)
export async function deleteOrder(
  id: string
): Promise<ApiResponse<OrderResponse>> {
  const response = await apiClient.delete<OrderResponse>(`/orders/${id}`);
  return response;
}

// Search orders by term with pagination
export async function searchOrders(
  searchTerm: string,
  page: number = 0,
  size: number = 20,
  sort: OrderSortOption = "orderDate,desc"
): Promise<ApiResponse<PageContent<OrderResponse>>> {
  const response = await apiClient.get<PageContent<OrderResponse>, object>(
    "/orders/search",
    {
      searchTerm,
      page,
      size,
      sort,
    }
  );
  return response;
}

// Get orders by customer ID with pagination
export async function getOrdersByCustomerId(
  customerId: string,
  page: number = 0,
  size: number = 20
): Promise<ApiResponse<PageContent<OrderResponse>>> {
  const response = await apiClient.get<PageContent<OrderResponse>, object>(
    `/orders/customer/${customerId}`,
    {
      page,
      size,
    }
  );
  return response;
}

// Get orders by date range with pagination
export async function getOrdersByDateRange(
  startDate: string,
  endDate: string,
  page: number = 0,
  size: number = 20
): Promise<ApiResponse<PageContent<OrderResponse>>> {
  const response = await apiClient.get<PageContent<OrderResponse>, object>(
    "/orders/date-range",
    {
      startDate,
      endDate,
      page,
      size,
    }
  );
  return response;
}

// Update order status
export async function updateOrderStatus(
  orderId: string,
  status: string
): Promise<ApiResponse<OrderResponse>> {
  const response = await apiClient.patch<OrderResponse, { status: string }>(
    `/orders/${orderId}/status`,
    { status }
  );
  return response;
}

// Update order payment status
export async function updateOrderPaymentStatus(
  orderId: string,
  paymentStatus: string
): Promise<ApiResponse<OrderResponse>> {
  const response = await apiClient.patch<
    OrderResponse,
    { paymentStatus: string }
  >(`/orders/${orderId}/payment-status`, { paymentStatus });
  return response;
}

// Generate order receipt
export async function generateOrderReceipt(orderId: string): Promise<
  ApiResponse<{
    receiptUrl: string;
    receiptId: string;
  }>
> {
  const response = await apiClient.post<
    { receiptUrl: string; receiptId: string },
    object
  >(`/orders/${orderId}/receipt`);
  return response;
}

// Utility functions for formatting
export const formatCurrency = (amount: string | number): string => {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(num)) return "$0.00";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

export const getOrderStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    draft: "bg-amber-500",
    confirmed: "bg-blue-500",
    paid: "bg-emerald-500",
    transferred: "bg-violet-500",
    delivering: "bg-indigo-500",
    delivered: "bg-teal-500",
    cancelled: "bg-red-500",
  };
  return colors[status] || "bg-gray-500";
};

export const getPaymentStatusColor = (status: string): string => {
  return status === "Paid"
    ? "text-emerald-600 border-emerald-200"
    : "text-amber-600 border-amber-200";
};

export async function confirmOrder(
  orderId: string
): Promise<ApiResponse<OrderResponse>> {
  const response = await apiClient.post<OrderResponse, object>(
    `/orders/${orderId}/confirm`
  );
  if (response.success) {
    return response;
  }
  throw new Error(response.message);
}

export async function addOrderItems(
  orderId: string,
  items: OrderItemCreateRequest[]
): Promise<ApiResponse<{ message: string }>> {
  return await apiClient.post<{ message: string }, OrderItemCreateRequest[]>(
    `/orders/${orderId}/add-items`,
    items
  );
}

export async function customerOrderHistory(customerId: string) {
  const response = await apiClient.get<OrderHistory, {}>(
    `/customers/${customerId}/orders`,
    {}
  );
  return response;
}

export async function markAsDraft(orderId: string) {
  const response = await apiClient.post<{}, {}>(
    `/orders/${orderId}/mark-as-draft`,
    {}
  );
  return response;
}

export async function capturePayment(
  orderId: string,
  payment: PaymentDialogValues
) {

  if (payment.mode == "COD") {
    const data = {
      paidAt: payment.paidAt,
    };
    const response = await apiClient.post<{}, {}>(
      `/orders/${orderId}/payments/mark-cod`,
      data
    );
    return response;
  } else {
    const response = await apiClient.post<{}, {}>(
      `/orders/${orderId}/payments/capture`,
      payment
    );
    return response;
  }
}
export async function getPaymentHistory(orderId: string) {
  const response = await apiClient.get<PaymentRecord[], {}>(
    `/orders/${orderId}/payments`
  );
  return response;
}
export async function markAsReadyForDelivery(orderId: string) {
  const response = await apiClient.post<{}, {}>(
    `/orders/${orderId}/mark-as-ready-for-delivery`
  );
  return response;
}
export async function markAsDelivered(orderId: string) {
  const response = await apiClient.post<{}, {}>(
    `/orders/${orderId}/mark-as-delivered`
  );
  return response;
}
export async function refundOrder(
  orderId: string,
  amount: number,
  reason: string
) {
  const data = {
    amount: amount,
    reason: reason,
  };
  const response = await apiClient.post<{}, {}>(
    `/orders/${orderId}/refund`,
    data
  );
  return response;
}

export async function returnOrder(
  orderId: string,
  restock: boolean,
  reason: string
) {
  const data = {
    restock: restock,
    reason: reason,
  };
  const response = await apiClient.post<{}, {}>(
    `/orders/${orderId}/return`,
    data
  );
  return response;
}

export async function printOrder(orderId: string) {
  const response = await apiClient.post<{}, {}>(`/orders/${orderId}/print`, {});
  return response;
}
export async function unPrintOrder(orderId: string) {
  const response = await apiClient.post<{}, {}>(`/orders/${orderId}/un-print`, {});
  return response;
}

export async function getOrderStateHistory(orderId: string) {
  const response = await apiClient.get<OrderStateHistory[], {}>(
    `/orders/${orderId}/states`,
    {}
  );
  return response;
}

export async function createOrderByComment(
  input: CommentOrderRequest
): Promise<ApiResponse<OrderResponse>> {
  const response = await apiClient.post<OrderResponse, CommentOrderRequest>(
    "/orders/comment",
    input
  );
  return response;
}

export async function createOrderByMessage(
  input: ChatOrderRequest
): Promise<ApiResponse<OrderResponse>> {
  const response = await apiClient.post<OrderResponse, {}>(
    "/orders/message",
    input
  );
  return response;
}
export const saleDateToDay = () => new Date().toISOString().slice(0, 10);
export async function getOrdersForSale(
  date: string,
  variantId?: string | null
): Promise<ApiResponse<PageContent<OrderResponse>>> {
  let url = `/orders/sales?date=${date}`;
  if (variantId) {
    url = `/orders/sales?date=${date}&variantId=${variantId}`;
  }
  const response = await apiClient.get<PageContent<OrderResponse>, {}>(url, {});
  return response;
}

export async function getSummaryOrder(date?: string | null): Promise<ApiResponse<OrderSummaryDaily>> {
  if (!date) {
    date = new Date().toISOString().split("T")[0];
  }
  const url = `/orders/summary/date?date=${date}`;
  const response = await apiClient.get<OrderSummaryDaily, {}>(url, {});
  return response;
}

export async function getSummaryOrderList(fromDate?: string, toDate?: string): Promise<ApiResponse<OrderSummaryDaily[]>> {
  const url = `/orders/summary/list?fromDate=${fromDate}&toDate=${toDate}`;
  const response = await apiClient.get<OrderSummaryDaily[], {}>(url, {});
  return response;
}

export async function getOrderReferences(id: string): Promise<ApiResponse<OrderReferenceItem[]>> {
  const url = `/orders/${id}/states/reference`;
  const response = await apiClient.get<OrderReferenceItem[], {}>(url, {});
  return response;
}
export async function getUsersForAsignment(): Promise<ApiResponse<User[]>> {
  const response = await apiClient.get<User[], {}>("/users/assign-list", {});
  return response;
}
export async function asignUser(orderId: string, username: string): Promise<ApiResponse<{ message: string }>> {
  const data = {
    orderId,
    username
  };
  const response = await apiClient.post<{ message: string }, {}>("/orders/assign", data);
  return response;

}
export async function cancelOrder(orderId: string): Promise<ApiResponse<{ message: string }>> {
  const response = await apiClient.post<{ message: string }, {}>(`/orders/${orderId}/cancel`);
  return response;
}

export async function refreshOrder(orderId: string): Promise<ApiResponse<{ message: string }>> {
  const response = await apiClient.get<{ message: string }, {}>(`/orders/${orderId}/refresh`);
  return response;
}
export async function getCancelOrderContent(orderId: string): Promise<ApiResponse<CancelOrder[]>> {
  const response = await apiClient.get<CancelOrder[], {}>(`/orders/cancel/${orderId}`);
  return response;
}

export async function updateOrderItem(itemId: string, updateRequest: UpdateOrderItem): Promise<ApiResponse<{}>> {
  const url = `/orderitems/${itemId}`;
  const response = await apiClient.put<{}, UpdateOrderItem>(url, updateRequest);
  return response;
}

export async function getOrderReceipt(
  id: string
): Promise<ApiResponse<OrderReceiptResponse>> {
  const response = await apiClient.get<OrderReceiptResponse, object>(
    `/orders/${id}/receipt`
  );
  return response;
}

export async function abaPaywayLink(orderId: string): Promise<ApiResponse<{ url: string }>> {
  const url = `/orders/${orderId}/aba-payway-link`;

  const response = await apiClient.get<{ url: string }, {}>(url, {});
  return response;
}
